/**
 * 系谱表迁移弹窗：挂点迁移 / 推荐线平移 二选一。
 */
import { Alert, Modal, Radio, Space, Typography } from 'antd';
import { useState } from 'react';
import { ShiftUplineForm } from './ShiftUplineModal';
import { OperatorForm } from './OperatorForm';

type Mode = 'placement' | 'shift_upline';

export type PlacementMovePreview = {
  user_id: number;
  username: string;
  move_subtree: boolean;
  binary_subtree_count: number;
  current_parent: { id: number; username: string; position: string | null } | null;
  new_parent: { id: number; username: string };
  new_position: 'L' | 'R';
  detached_children: Array<{ id: number; username: string; position: string | null }>;
  displaced: Array<{ id: number; parent_id: number; position: 'L' | 'R' }>;
  note: string;
  would_apply: boolean;
};

function PlacementMoveForm({
  userId,
  username,
  onDone,
  forceSubtreeChoice,
}: {
  userId: number;
  username: string;
  onDone?: () => void;
  /** 非全球挂回：必须手选是否带子树，不设默认 */
  forceSubtreeChoice?: boolean;
}) {
  return (
    <OperatorForm<PlacementMovePreview>
      endpoint={`/api/admin/users/${userId}/placement-move`}
      modalWidth={720}
      fields={[
        {
          name: 'confirm_username',
          label: '确认用户名（须与待迁账户完全一致）',
          type: 'text',
          required: true,
          placeholder: username,
        },
        {
          name: 'new_parent',
          label: '新安置父节点（用户名或玩家编号）',
          type: 'text',
          required: true,
          placeholder: '如 100349 或 uo…',
        },
        {
          name: 'position',
          label: '左右区',
          type: 'select',
          required: true,
          initialValue: 'L',
          options: [
            { value: 'L', label: '左区 L' },
            { value: 'R', label: '右区 R' },
          ],
        },
        {
          name: 'move_subtree',
          label: forceSubtreeChoice
            ? '是否带子树（必选）'
            : '是否带子树',
          type: 'select',
          required: true,
          ...(forceSubtreeChoice ? {} : { initialValue: 1 }),
          options: [
            { value: 1, label: '是 — 整棵二叉子树随迁' },
            { value: 0, label: '否 — 直挂下级先改挂到原父网体，再只迁本节点' },
          ],
          placeholder: forceSubtreeChoice ? '请选择：整棵搬 / 只搬本人' : undefined,
        },
        {
          name: 'note',
          label: '操作备注',
          type: 'textarea',
          required: true,
          placeholder: `挂点迁移 ${username}…`,
        },
      ]}
      submitLabel="预览挂点迁移"
      renderPreview={(p) => (
        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="仅改二叉挂点，不改推荐上级。目标位若被占用会先腾空（滑落）。"
          />
          <Typography.Paragraph>
            #{p.user_id} {p.username}
            {p.current_parent
              ? `：${p.current_parent.username}[${p.current_parent.position ?? '—'}]`
              : '：根'}{' '}
            → {p.new_parent.username}[{p.new_position}]
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            二叉子树节点数（含自身）：{p.binary_subtree_count} ·{' '}
            {p.move_subtree ? '带子树随迁' : '不带子树'}
          </Typography.Paragraph>
          {!p.move_subtree && p.detached_children.length > 0 ? (
            <Typography.Paragraph>
              将拆挂直挂下级：
              {p.detached_children.map((c) => (
                <div key={c.id}>
                  #{c.id} {c.username} [{c.position ?? '—'}]
                </div>
              ))}
            </Typography.Paragraph>
          ) : null}
          {p.displaced.length > 0 ? (
            <Typography.Paragraph type="warning">
              目标位当前有占用（执行时会滑落腾空），涉及 {p.displaced.length} 个节点。
            </Typography.Paragraph>
          ) : null}
          <Typography.Paragraph type="secondary">备注：{p.note}</Typography.Paragraph>
        </div>
      )}
      onDone={onDone}
    />
  );
}

export function MigrateUserModal({
  open,
  userId,
  username,
  onClose,
  onDone,
  placementOnly = false,
  forceSubtreeChoice = false,
}: {
  open: boolean;
  userId: number;
  username: string;
  onClose: () => void;
  onDone?: () => void;
  /** 仅挂点迁移（非全球挂回） */
  placementOnly?: boolean;
  /** 强制手选是否带子树 */
  forceSubtreeChoice?: boolean;
}) {
  const [mode, setMode] = useState<Mode>('placement');

  return (
    <Modal
      open={open}
      title={placementOnly ? `挂回 — ${username}` : `迁移 — ${username}`}
      footer={null}
      onCancel={onClose}
      destroyOnClose
      width={mode === 'shift_upline' && !placementOnly ? 920 : 720}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {placementOnly ? (
          <Alert
            type="warning"
            showIcon
            message="将本账户挂入全球网体。是否带子树必须手选，无默认。"
          />
        ) : (
          <div>
            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              迁移类型
            </Typography.Text>
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { value: 'placement', label: '挂点迁移（仅安置）' },
                { value: 'shift_upline', label: '推荐线平移' },
              ]}
            />
          </div>
        )}
        {placementOnly || mode === 'placement' ? (
          <PlacementMoveForm
            userId={userId}
            username={username}
            forceSubtreeChoice={forceSubtreeChoice || placementOnly}
            onDone={() => {
              onDone?.();
              onClose();
            }}
          />
        ) : (
          <ShiftUplineForm
            userId={userId}
            username={username}
            onDone={() => {
              onDone?.();
              onClose();
            }}
          />
        )}
      </Space>
    </Modal>
  );
}
